/**
 * @fileOverview The main entry point to your standalone. Start editing here.
 */

import React, { useEffect, useState } from 'react'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/reset.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/site.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/segment.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/container.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/header.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/image.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/list.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/label.css'
import './styles.scss'
import { TypescriptComponent } from 'components/typescriptExample'
import { Masthead } from '@ta-interaktiv/react-masthead'
import { Footer } from '@ta-interaktiv/react-footer'
import { ThemeSwitch } from '@ta-interaktiv/react-theme-switch'
import { qanda } from './qanda'
import {
  EstimateQuestion,
  SingleChoice,
  Slider,
  SwissTileMap,
  ColumnChart,
  BarChart,
  Summary,
  SwissTileResolve,
  voteComparison,
  voteQuality,
  convertPercentToWords,
  capitalize
} from 'components/UserEstimates'
import { GlobalColors } from 'components/taCSScolors'

// TODO Set the correct article ID here
const ARTICLE_ID = '107956859044'

export function AppComponent() {
  const [questionAnswered, setAnswered] = useState<string[]>([])
  const handleAnswer = (questionid: string) => {
    setAnswered(questionAnswered => [...questionAnswered, questionid])
  }

  const digitFormat = new Intl.NumberFormat('de-DE', {
    style: 'decimal',
    minimumFractionDigits: 0
  }).format

  const SliderAnswers = ({
    compare,
    quality,
    props,
    absTotal,
    percentSameAsUser,
    showCorrect
  }: {
    compare: { closer: { share: number }; further: { share: number } }
    quality: { percentOff: number; words: string }
    props: { correctAnswer: number }
    absTotal: number
    percentSameAsUser: number
    showCorrect?: number | string
  }) => {
    return (
      <div className='textmessage'>
        <div className={'message ' + (quality.percentOff > 25 ? 'bad' : '')}>
          {quality.words} Die richtige Antwort lautet{' '}
          {showCorrect ? showCorrect : digitFormat(props.correctAnswer)}%.
        </div>
        {compare.closer.share !== 0 ? (
          compare.closer.share > compare.further.share ? (
            <>
              <b>{compare.closer.share}% </b>der <b>{absTotal} Teilnehmenden</b>{' '}
              waren näher am richtigen Wert.
            </>
          ) : (
            <>
              <b>{compare.closer.share}% </b>der <b>{absTotal} Teilnehmenden</b>{' '}
              waren{' '}
              {(quality.words === 'Knapp daneben!' ||
                quality.words === 'Sehr gut!') &&
                'noch'}{' '}
              näher am richtigen Wert.
            </>
          )
        ) : (
          <>
            <b>{percentSameAsUser}%</b> der <b>{absTotal} Teilnehmenden</b>{' '}
            lagen ebenfalls richtig.
          </>
        )}
        {/* {compare.closer.share === 0 ?  : (<>Geht noch besser</>)} */}
      </div>
    )
  }
  const TileMapAnswer = ({
    userWasCorrect,
    percentSameAsUser,
    absTotal,
    correctText
  }: {
    userWasCorrect: boolean
    percentSameAsUser: number
    absTotal: number
    correctText: string
  }) => {
    return (
      <div className='textmessage'>
        <div className={'message ' + (!userWasCorrect ? 'bad' : '')}>
          {!userWasCorrect ? 'Leider falsch!' : 'Richtig!'} {correctText}
        </div>
        Die gleiche Antwort gaben <b>{percentSameAsUser}%</b> der{' '}
        <b>{absTotal} Teilnehmenden</b>.{' '}
        {/* {userWasCorrect ? 'richtig' : 'daneben'}. */}
      </div>
    )
  }
  return (
    <div className='index'>
      <Masthead articleId={ARTICLE_ID} />
      <ThemeSwitch />
      <GlobalColors />

      <header className='ui vertical  very fitted  segment'>
        <div className='ui container'>
          <div className='ui header'>
            <h1>Wo wohnen am meisten Millionäre?</h1>
            <h2>
              Beim Vermögen ist die Kluft zwischen Arm und Reich tiefer als beim
              Einkommen. Wo ist sie am extremsten? Und welcher Kanton
              profitiert? Erraten Sie es in unserem Quiz.
            </h2>
          </div>
        </div>
      </header>

      <div className='ui vertical segment'>
        <div className='ui text container'>
          <div className='ta metainfo'>
            <div className='ta author'>
              Iwan Städler, Patrick Vögeli, Sebastian Broschinski
            </div>
            <div className='ta updated'>Aktualisiert am 30. Oktober 2020</div>
          </div>
          <div className='ta text'>
            <p>
              Immer wieder spricht die Schweiz über die Spitzensaläre der
              Manager – auch im Vergleich zu den Löhnen der Schlechtverdiener.
              Weniger Aufsehen erregt dagegen die Verteilung der Vermögen.
              Obwohl die Unterschiede hier viel grösser sind als bei den
              Einkommen. Dies zeigt die neuste Vermögensstatistik des Bundes.
              Die Eidgenössische Steuerverwaltung hat dafür sämtliche
              Veranlagungen per Ende 2017 ausgewertet. Aktuellere Zahlen gibt es
              noch nicht, da das Veranlagen bekanntlich seine Zeit braucht.
            </p>
            <p>
              Erraten Sie in unserem Quiz, wo es den Millionären am besten
              gefällt. In welchem Kanton das Vermögen am ungleichsten verteilt
              ist. Und wie viel für die ärmere Hälfte bleibt. Vergleichen Sie
              Ihre Schätzung mit jener anderer Leserinnen. Und erfahren Sie
              nebenbei viel Interessantes über die Schweizer Reichen – und wie
              die Kantone um diese buhlen.
            </p>
            {/* <div>{qanda[2].erklaerung}</div> */}
          </div>
        </div>
      </div>

      <div id='quizSection'>
        <div className='ui vertical relaxed segment'>
          <div className='ui text container'>
            <div className='estimateContainer'>
              <EstimateQuestion
                articleID={ARTICLE_ID}
                questionID='q1'
                question={qanda[0].question}
                correctAnswer={qanda[0].correctAnswer}
                askTheQuestion={props => (
                  <Slider
                    {...props}
                    range={[0, 100]}
                    step={0.5}
                    unit='%'
                    start={50}
                  />
                )}
                // ignoreLocalStorage
                answerTheQuestion={props => (
                  <>
                    <Summary
                      {...props}
                      text={(absTotal, percentSameAsUser, c) => {
                        const compare = voteComparison(
                          props.allAnswers,
                          +props.correctAnswer,
                          +props.userAnswer.replace('_', '.')
                        )
                        const quality = voteQuality(
                          [0, 100],
                          +props.correctAnswer,
                          +props.userAnswer.replace('_', '.')
                        )
                        return (
                          <SliderAnswers
                            compare={compare}
                            quality={quality}
                            props={props}
                            absTotal={absTotal}
                            percentSameAsUser={percentSameAsUser}
                            showCorrect={'1,4'}
                          />
                        )
                      }}
                    />
                    <ColumnChart
                      {...props}
                      range={[0, 100]}
                      step={0.5}
                      showCorrect={'1,4'}
                    />
                  </>
                )}
                reportsuccess={handleAnswer}
              />
              {questionAnswered.includes('q1') && (
                <div className=' content'>{qanda[0].erklaerung}</div>
              )}
            </div>

            <div className='estimateContainer'>
              <EstimateQuestion
                articleID={ARTICLE_ID}
                questionID='q2'
                question={qanda[1].question}
                correctAnswer={qanda[1].correctAnswer}
                askTheQuestion={props => (
                  <Slider
                    {...props}
                    range={[0, 100]}
                    step={1}
                    unit='%'
                    start={0}
                  />
                )}
                // ignoreLocalStorage
                answerTheQuestion={props => (
                  <>
                    <Summary
                      {...props}
                      text={(absTotal, percentSameAsUser, c) => {
                        const compare = voteComparison(
                          props.allAnswers,
                          +props.correctAnswer,
                          +props.userAnswer.replace('_', '.')
                        )
                        const quality = voteQuality(
                          [0, 100],
                          +props.correctAnswer,
                          +props.userAnswer.replace('_', '.')
                        )
                        return (
                          <SliderAnswers
                            compare={compare}
                            quality={quality}
                            props={props}
                            absTotal={absTotal}
                            percentSameAsUser={percentSameAsUser}
                          />
                        )
                      }}
                    />
                    <ColumnChart {...props} range={[0, 100]} step={1} />
                  </>
                )}
                reportsuccess={handleAnswer}
              />
              {questionAnswered.includes('q2') && (
                <div className=' content'>{qanda[1].erklaerung}</div>
              )}
            </div>

            <div className='estimateContainer'>
              <EstimateQuestion
                articleID={ARTICLE_ID}
                questionID='q3'
                question={qanda[2].question}
                correctAnswer={qanda[2].correctAnswer}
                askTheQuestion={props => <SwissTileMap {...props} />}
                // ignoreLocalStorage
                answerTheQuestion={props => (
                  <>
                    <Summary
                      {...props}
                      text={(
                        absTotal: number,
                        percentSameAsUser: number,
                        userWasCorrect: boolean
                      ) => {
                        return (
                          <TileMapAnswer
                            userWasCorrect={userWasCorrect}
                            percentSameAsUser={percentSameAsUser}
                            absTotal={absTotal}
                            correctText={
                              'Im Kanton Zug ist die Millionärsdichte am höchsten.'
                            }
                          />
                        )
                      }}
                    />
                    <SwissTileResolve {...props} />
                  </>
                )}
                reportsuccess={handleAnswer}
              />
              {questionAnswered.includes('q3') && (
                <div className=' content'>{qanda[2].erklaerung}</div>
              )}
            </div>

            <div className='estimateContainer'>
              <EstimateQuestion
                articleID={ARTICLE_ID}
                questionID='q4'
                question={qanda[3].question}
                correctAnswer={qanda[3].correctAnswer}
                askTheQuestion={props => <SwissTileMap {...props} />}
                // ignoreLocalStorage
                answerTheQuestion={props => (
                  <>
                    <Summary
                      {...props}
                      text={(
                        absTotal: number,
                        percentSameAsUser: number,
                        userWasCorrect: boolean
                      ) => {
                        return (
                          <TileMapAnswer
                            userWasCorrect={userWasCorrect}
                            percentSameAsUser={percentSameAsUser}
                            absTotal={absTotal}
                            correctText={
                              'Im Kanton Nidwalden ist das durchschnittliche Vermögen am höchsten.'
                            }
                          />
                        )
                      }}
                    />
                    <SwissTileResolve {...props} />
                  </>
                )}
                reportsuccess={handleAnswer}
              />
              {questionAnswered.includes('q4') && (
                <div className=' content'>{qanda[3].erklaerung}</div>
              )}
            </div>

            <div className='estimateContainer'>
              <EstimateQuestion
                articleID={ARTICLE_ID}
                questionID='q5'
                question={qanda[4].question}
                correctAnswer={qanda[4].correctAnswer}
                askTheQuestion={props => <SwissTileMap {...props} />}
                // ignoreLocalStorage
                answerTheQuestion={props => (
                  <>
                    <Summary
                      {...props}
                      text={(
                        absTotal: number,
                        percentSameAsUser: number,
                        userWasCorrect: boolean
                      ) => {
                        return (
                          <TileMapAnswer
                            userWasCorrect={userWasCorrect}
                            percentSameAsUser={percentSameAsUser}
                            absTotal={absTotal}
                            correctText={
                              'Im Kanton Nidwalden zahlt man am wenigsten Vermögenssteuern.'
                            }
                          />
                        )
                      }}
                    />
                    <SwissTileResolve {...props} />
                  </>
                )}
                reportsuccess={handleAnswer}
              />
              {questionAnswered.includes('q5') && (
                <div className=' content'>{qanda[4].erklaerung}</div>
              )}
            </div>

            <div className='estimateContainer'>
              <EstimateQuestion
                articleID={ARTICLE_ID}
                questionID='q6'
                question='Wie haben sich die Vermögenssteuern in den letzten 20 Jahren entwickelt?'
                options={qanda[5].options}
                correctAnswer={qanda[5].options[1]}
                askTheQuestion={props => <SingleChoice {...props} />}
                // ignoreLocalStorage
                answerTheQuestion={props => (
                  <>
                    <Summary
                      {...props}
                      text={(
                        absTotal: number,
                        percentSameAsUser: number,
                        userWasCorrect: boolean
                      ) => {
                        return (
                          <div className='textmessage'>
                            <div
                              className={
                                'message ' + (!userWasCorrect ? 'bad' : '')
                              }
                            >
                              {!userWasCorrect ? 'Leider falsch!' : 'Richtig!'}{' '}
                              {qanda[5].options[1]}.
                            </div>
                            Die gleiche Antwort gaben{' '}
                            <b>{percentSameAsUser}%</b> der{' '}
                            <b>{absTotal} Teilnehmenden</b>.{' '}
                          </div>
                        )
                      }}
                    />
                    <BarChart {...props} />
                  </>
                )}
                reportsuccess={handleAnswer}
              />
              {questionAnswered.includes('q6') && (
                <div className=' content'>{qanda[5].erklaerung}</div>
              )}
            </div>

            <div className='estimateContainer'>
              <EstimateQuestion
                articleID={ARTICLE_ID}
                questionID='q7'
                question={qanda[6].question}
                correctAnswer={qanda[6].correctAnswer}
                askTheQuestion={props => <SwissTileMap {...props} />}
                // ignoreLocalStorage
                answerTheQuestion={props => (
                  <>
                    <Summary
                      {...props}
                      text={(
                        absTotal: number,
                        percentSameAsUser: number,
                        userWasCorrect: boolean
                      ) => {
                        return (
                          <TileMapAnswer
                            userWasCorrect={userWasCorrect}
                            percentSameAsUser={percentSameAsUser}
                            absTotal={absTotal}
                            correctText={
                              'Im Kanton Obwalden ist das Durchschnittsvermögen seit 2003 am stärksten gestiegen.'
                            }
                          />
                        )
                      }}
                    />
                    <SwissTileResolve {...props} />
                  </>
                )}
                reportsuccess={handleAnswer}
              />
              {questionAnswered.includes('q7') && (
                <div className=' content'>{qanda[6].erklaerung}</div>
              )}
            </div>

            <div className='estimateContainer'>
              <EstimateQuestion
                articleID={ARTICLE_ID}
                questionID='q8'
                question={qanda[7].question}
                correctAnswer={qanda[7].correctAnswer}
                askTheQuestion={props => <SwissTileMap {...props} />}
                // ignoreLocalStorage
                answerTheQuestion={props => (
                  <>
                    <Summary
                      {...props}
                      text={(
                        absTotal: number,
                        percentSameAsUser: number,
                        userWasCorrect: boolean
                      ) => {
                        return (
                          <TileMapAnswer
                            userWasCorrect={userWasCorrect}
                            percentSameAsUser={percentSameAsUser}
                            absTotal={absTotal}
                            correctText={
                              'Im Kanton Genf sind die Vermögen am ungleichsten verteilt.'
                            }
                          />
                        )
                      }}
                    />
                    <SwissTileResolve {...props} />
                  </>
                )}
                reportsuccess={handleAnswer}
              />
              {questionAnswered.includes('q8') && (
                <div className=' content'>{qanda[7].erklaerung}</div>
              )}
            </div>
          </div>
        </div>
      </div>

      <Footer
        articleId={ARTICLE_ID}
        questions={[
          'Wie informativ war der Beitrag?',
          'Wie unterhaltsam war das Quiz?',
          'Wie gut funktionierte die technische Umsetzung?'
        ]}
        credits={[
          ['Konzept und Text', 'Iwan Städler'],
          [
            'Tilemap der Schweiz',
            'auch inspiriert von <a href="https://github.com/ebp-group/Switzerland_Tilemap" target="_blank">Ralph Straumann</a>'
          ],
          ['Umsetzung', 'Patrick Vögeli, Sebastian Broschinski']
        ]}
      />
    </div>
  )
}
